<!--
  The whole coach-review screen: header, card and button bar. The check-up being reviewed
  is always one the client has already submitted, so the header never shows a step counter
  and never uses the wizard's full-width layout.

  While the coaching dashboard switch is on (#5456) the header carries tabs. The Decision
  content is hidden, never removed, while another tab shows, so a number the coach typed
  survives a look at the Body Composition or Training tab. The button bar shows on every tab.
-->
<cs-header [backButton]="true" [fullWidth]="false" [separator]="true">
  <cs-header-title centered>
    {{ LL.coach.checkup.reviewTitle() }}
  </cs-header-title>
  @if (coachingDashboardOn) {
  <cs-header-content>
    <cs-segment class="review-tabs" [selection]="tab" (selectionChange)="onTabPicked($event)">
      <cs-segment-button [value]="CoachReviewTab.Decision">
        {{ LL.coach.checkup.tabDecision() }}
      </cs-segment-button>
      <cs-segment-button [value]="CoachReviewTab.BodyComposition">
        {{ LL.coach.checkup.tabBodyComposition() }}
      </cs-segment-button>
      <cs-segment-button [value]="CoachReviewTab.Training">
        {{ LL.trainingOverview.tabTraining() }}
      </cs-segment-button>
    </cs-segment>
  </cs-header-content>
  }
</cs-header>

<cs-content>
  <cs-card-list class="decision-tab" [hidden]="tab !== CoachReviewTab.Decision">
    <cs-card>
      <cs-card-title>
        {{ LL.coach.checkup.reviewTitle() }}
      </cs-card-title>
      <cs-card-content>
        <cs-note big light>
          {{ LL.coach.checkup.reviewActionPrompt() }}
        </cs-note>
        <cs-item-list>
          <div class="status-section reported-intake">
            @if (coachingDashboardOn) {
            <cs-label class="section-label">{{ LL.coach.checkup.intakeReportedByClient() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ LL.coach.checkup.kcalValue({ value: currentTargetDisplay() }) }}</span>
            </div>
            <cs-note light class="section-source">{{ reportedIntakeSourceText() }}</cs-note>
            } @else {
            <cs-label class="section-label">{{ LL.coach.checkup.currentTarget() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ LL.coach.checkup.kcalValue({ value: currentTargetDisplay() }) }}</span>
            </div>
            }
          </div>
          <div class="status-section recommended-target">
            <cs-label class="section-label">{{ LL.coach.checkup.recommendedTarget() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ LL.coach.checkup.kcalValue({ value: recommendedTargetDisplay() }) }} ({{ coachingDashboardOn ? recommendedChangeDisplay() : recommendedDeltaDisplay() }})</span>
            </div>
          </div>
          @if (coachingDashboardOn && weightChangeDisplay(); as weightChange) {
          <div class="status-section weight-change">
            <cs-label class="section-label">{{ LL.coach.checkup.weightChange() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ weightChange }}</span>
            </div>
          </div>
          }
          @if (checkup.bfData?.bf_pc) {
          <div class="status-section">
            <cs-label class="section-label">{{ LL.weeklyCheckup.labels.estimatedBodyfat() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ LL.coach.checkup.bfPercent({ value: checkup.bfData.bf_pc }) }}</span>
            </div>
          </div>
          }
        </cs-item-list>

        @if (coachingDashboardOn) {
        <!-- #5456: the coach's answer is picked here; the bar's Confirm sends it -->
        <cs-radio-group class="decision-choices" [selected]="decision" (selectedChange)="onDecisionPicked($event)">
          @for (choice of decisionChoices; track choice) {
          <cs-radio [value]="choice" [attr.data-choice]="choice" [disabled]="isInFlight">
            {{ decisionLabel(choice) }}
            <span item-right class="choice-kcal">{{ decisionKcal(choice) }}</span>
            <cs-note light>{{ decisionHelp(choice) }}</cs-note>
          </cs-radio>
          @if (choice === CoachResolvedAction.Modified && decision === CoachResolvedAction.Modified) {
          <cs-item-list class="coach-target-fields">
            <cs-calorie-change-input #coachCalorieChange [baseCalories]="checkup.calorieIntakeBaseline"
              [minimumDailyIntake]="minimumDailyIntake()" [defaultDirection]="defaultChangeDirection()"
              [audience]="CalorieChangeAudience.Coach" [(calories)]="customCalories"></cs-calorie-change-input>
          </cs-item-list>
          }
          }
        </cs-radio-group>
        } @else if (reviewMode === 'modify') {
        <cs-item-list>
          <cs-item-group>
            <cs-note>{{ LL.coach.checkup.customCalories() }}</cs-note>
            <cs-input-measure #coachModifyInput [(canonValue)]="customCalories"
              type="nutrient_kcal"></cs-input-measure>
          </cs-item-group>
        </cs-item-list>
        }
      </cs-card-content>
    </cs-card>
  </cs-card-list>

  <!-- the Body Composition tab: drawn once the coach has opened it, then kept and only hidden -->
  @if (coachingDashboardOn && overviewRequested) {
  <cs-card-list class="body-composition-tab" [hidden]="tab !== CoachReviewTab.BodyComposition">
    <cs-body-comp-overview [model]="overviewModel" [loading]="overviewLoading" [failed]="overviewFailed"
      (retry)="loadOverviewAsync()">
    </cs-body-comp-overview>
  </cs-card-list>
  }

  <!-- the Training tab (phase 3): the client's Training Overview up to today, drawn once the coach
       has opened it, then kept and only hidden. A tap on an exercise opens the same menu as on the page -->
  @if (coachingDashboardOn && trainingRequested) {
  <!-- a plain column, not a cs-card-list: that list places only cards, and drew the head line beside the grid (#6675) -->
  <div class="training-tab" [hidden]="tab !== CoachReviewTab.Training">
    <div class="training-tab-head">
      <cs-note light class="up-to-today">{{ LL.trainingOverview.upToToday() }}</cs-note>
      <cs-button small secondary [label]="LL.trainingOverview.openOverview()" (click)="onOpenTrainingOverviewClick($event)"></cs-button>
    </div>
    <cs-training-overview [model]="trainingModel" [busy]="trainingLoading" [failed]="trainingFailed"
      (retry)="loadTrainingAsync()" (rowTap)="onTrainingRowTap($event)">
    </cs-training-overview>
  </div>
  }
</cs-content>

<cs-page-action-buttons>
  @if (coachingDashboardOn) {
  <!-- #5456: one button; on another tab with no valid answer picked it brings the Decision tab forward -->
  <cs-button medium primary class="confirm-decision" [label]="LL.common.buttons.confirm()"
    [disabled]="isConfirmDisabled()" (click)="onConfirmClick($event)">
  </cs-button>
  } @else if (reviewMode === 'summary') {
  <cs-button medium secondary [label]="LL.coach.checkup.decline()" (click)="onDeclineClick($event)">
  </cs-button>
  <cs-button medium secondary [label]="LL.coach.checkup.modify()" (click)="onModifyClick($event)">
  </cs-button>
  <cs-button medium primary [label]="LL.coach.checkup.accept()" (click)="onAcceptClick($event)">
  </cs-button>
  } @else {
  <cs-button medium secondary [label]="LL.common.buttons.cancel()" (click)="onCancelModifyClick($event)">
  </cs-button>
  <cs-button medium primary [label]="LL.common.buttons.apply()" [disabled]="!customCalories || customCalories <= 0"
    (click)="onApplyModifyClick($event)">
  </cs-button>
  }
</cs-page-action-buttons>
